/* eslint-disable @typescript-eslint/explicit-function-return-type */
import Resizable from '@corvu/resizable';
import { ViewDetailsResponse } from '@data-access/connect/tc-tcps';
import {
  MENU_PANEL_WIDTH_PX,
  DEFAULT_PANEL_WIDTH_PX,
  FileFolderItem,
  MAX_PANEL_WIDTH_PX,
  MIN_PANEL_WIDTH_PX,
  MissionDetailsSelection,
  PaneContextProvider,
  RightPanelTabName,
  RunDetailsSelection,
  SidePanelTabName,
  useAppContext,
  useCapabilityRegistryContext,
  useDataContext,
  EXPERIMENTS_KEYS,
  useFeatureExtractionContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { ErrorBoundaryHandler } from '@trimble/solidjs-components';
import { TrimblePageHeader } from '@trimble/page-header';
import { PortalMountProvider } from '@trimble/ui-components/solidjs';
import {
  createEffect,
  createMemo,
  createRoot,
  createSignal,
  JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { ActiveTab, RightPanel } from './side-panel/RightPanel';
import { SidePanel } from './side-panel/SidePanel';
import { NewExtractionDialog } from './selections/tree/NewExtractionDialog';
import { Web3DContainer } from './Web3DContainer';
import { MainToolbar } from './main-toolbar/MainToolbar';
import { UnreachableCaseError } from 'ts-essentials';
import { isItemLoadingOrLoaded, LoadState } from '@trimble/data-loading';
import { t } from '@trimble/i18n';
import { useConnectProject } from '@trimble/connect-project-providers';
import { ObjectPropertiesPanelCapability } from '@trimble/web3d-capabilities';
import { useMainToolbarMeasurements } from './main-toolbar/useMainToolbarMeasurements';
import { useMainToolbarSnapping } from './main-toolbar/useMainToolbarSnapping';
import { useToolbarClipState } from '@trimble/toolbar-clip-state';
import { useToolbarMarkupState } from '@trimble/toolbar-markup-state';
import { useMainToolbarSemiAutomaticDetection } from './main-toolbar/useMainToolbarSemiAutomaticDetection';
import { LineDetectionSettingsPanel } from './main-toolbar/LineDetectionSettingsPanel';
import { useGlobalToolbarItems } from './main-toolbar/useGlobalToolbarItems';

const MAIN_WINDOW_PANEL_COUNT = 3;

export interface MainWindowProps {
  web3DPath: string;
  helpUrl?: string;
  profileUrl: string;
  urlSyncComplete?(): boolean;
}

export const MainWindow = (props: MainWindowProps): JSX.Element => {
  const {
    selectedSidePanelTab,
    setSelectedSidePanelTab,
    sidePanelWidthPx,
    setSidePanelWidthPx,
    selectedRightPanelTab,
    setSelectedRightPanelTab,
    rightPanelWidthPx,
    setRightPanelWidthPx,
    experiments,
  } = useAppContext();

  const { dataState } = useDataContext();

  const {
    selectedProjectId,
    selectedProject,
    selectedRegion,
    selectedProjectExplorerUrl,
  } = useConnectProject();

  createEffect(
    on(selectedProjectId, (project, prevProject) => {
      if (prevProject === undefined || project === undefined) {
        return;
      }
      const region = selectedRegion();
      if (!region) return;

      const url = new URL(window.location.href);
      const newTenantTrn = `trn:connect:projects:${region.location}:${project}`;
      url.searchParams.set('tenantTrn', newTenantTrn);
      window.location.href = url.toString();
    })
  );

  const { capabilityRegistry } = useCapabilityRegistryContext();

  const featureExtraction = useFeatureExtractionContext();
  const fxWorkspace = useFxWorkspaceContext();

  const [selectedViewDetails, setSelectedViewDetails] = createSignal<
    ViewDetailsResponse | undefined
  >(undefined);

  const [selectedModelDetails, setSelectedModelDetails] = createSignal<
    FileFolderItem | undefined
  >(undefined);

  const [selectedRunDetails, setSelectedRunDetails] = createSignal<
    RunDetailsSelection | undefined
  >(undefined);

  const [selectedMissionDetails, setSelectedMissionDetails] = createSignal<
    MissionDetailsSelection | undefined
  >(undefined);

  const objectPropertiesPanelCapability = capabilityRegistry.get(
    ObjectPropertiesPanelCapability
  );

  // Track window width for responsive panel sizing
  const [windowWidth, setWindowWidth] = createSignal(window.innerWidth);
  const [createTopicRequest, setCreateTopicRequest] = createSignal<
    { readonly type: 'create-topic'; readonly id: number } | undefined
  >(undefined);
  const [createTopicRequestId, setCreateTopicRequestId] = createSignal(0);
  const [portalMount, setPortalMount] = createSignal<HTMLElement | undefined>();

  const measurements = useMainToolbarMeasurements({
    selectedRightPanelTab,
    setSelectedRightPanelTab,
  });
  const markups = useToolbarMarkupState(capabilityRegistry);
  const clipping = useToolbarClipState(capabilityRegistry);
  const snapping = useMainToolbarSnapping();
  const semiAutomaticDetection = useMainToolbarSemiAutomaticDetection();
  const isLineDetectionEnabled = createMemo(
    () => experiments()[EXPERIMENTS_KEYS['featureExtractionPOC']] ?? false
  );

  const globalToolbarItems = useGlobalToolbarItems({
    selectedRightPanelTab,
    setSelectedRightPanelTab,
    setSelectedSidePanelTab,
    createTopicRequestId,
    setCreateTopicRequestId,
    setCreateTopicRequest,
    measurements,
    markups,
    clipping,
    snapping,
    semiAutomaticDetection,
    isLineDetectionEnabled,
  });

  const activeRightPanelTab = createMemo((): ActiveTab | undefined => {
    const tab = selectedRightPanelTab();
    if (!tab) return undefined;

    // Keep these outside of the switch statement.
    // This ensures that when (item)Details change and activeTab does not, the memo will still run.
    const viewDetails = selectedViewDetails();
    const modelDetails = selectedModelDetails();
    const runDetails = selectedRunDetails();
    const missionDetails = selectedMissionDetails();
    const extractionWorkflow = fxWorkspace.selectedExtractionWorkflow();

    switch (tab) {
      case RightPanelTabName.ViewDetails:
        return viewDetails
          ? { tab: RightPanelTabName.ViewDetails, data: viewDetails }
          : undefined;

      case RightPanelTabName.ModelDetails:
        return modelDetails
          ? {
              tab: RightPanelTabName.ModelDetails,
              data: {
                projectExplorerUrl: selectedProjectExplorerUrl() ?? '#',
                model: modelDetails,
              },
            }
          : undefined;

      case RightPanelTabName.RunDetails:
        return runDetails
          ? { tab: RightPanelTabName.RunDetails, data: runDetails }
          : undefined;

      case RightPanelTabName.MissionDetails:
        return missionDetails
          ? { tab: RightPanelTabName.MissionDetails, data: missionDetails }
          : undefined;

      case RightPanelTabName.ExtractionWorkflow:
        return extractionWorkflow !== undefined
          ? { tab: RightPanelTabName.ExtractionWorkflow }
          : undefined;

      case RightPanelTabName.CreateView:
      case RightPanelTabName.MeasurementDetails:
      case RightPanelTabName.ObjectProperties:
      case RightPanelTabName.FeatureProperties:
      case RightPanelTabName.Layout:
      case RightPanelTabName.Settings:
        return { tab };

      default:
        throw new UnreachableCaseError(tab);
    }
  });

  const isSidePanelOpen = createMemo(() => {
    const sidePanelTab = selectedSidePanelTab();
    return sidePanelTab !== undefined;
  });

  const isRightPanelOpen = createMemo(() => {
    return activeRightPanelTab() !== undefined;
  });

  /** Panels size array in percentages */
  const paneSizes = createMemo(() => {
    const sideWidthPx = isSidePanelOpen()
      ? clampPanelWidth(sidePanelWidthPx())
      : MENU_PANEL_WIDTH_PX;

    const rightWidthPx = isRightPanelOpen()
      ? clampPanelWidth(rightPanelWidthPx())
      : 0;

    const windowWidthPx = windowWidth();
    const side = sideWidthPx / windowWidthPx;
    const right = rightWidthPx / windowWidthPx;
    const main = 1 - side - right;

    return [side, main > 0 ? main : 0, right];
  });

  // Panels resize independently, main panel absorbs the difference
  const onSizesChange = (newSizes: number[]) => {
    // We expect an array size of 3, but when there is an error rendering
    // a component in a resizable panel, the array of sizes can be fewer.
    if (newSizes.length !== MAIN_WINDOW_PANEL_COUNT) return;

    // Handle bug in corvu resizable where the sizes can be greater than 1
    const sizes = newSizes.reduce((acc, curr) => acc + curr, 0);
    if (sizes > 1) return;

    const [side, , right] = newSizes;

    if (side && isSidePanelOpen()) {
      const sidePx = clampPanelWidth(side * windowWidth());
      setSidePanelWidthPx(sidePx);
    }

    if (right && isRightPanelOpen()) {
      const rightPx = clampPanelWidth(right * windowWidth());
      setRightPanelWidthPx(rightPx);
    }
  };

  const handleViewDetailsSelect = (view: ViewDetailsResponse): void => {
    setSelectedViewDetails(view);
    setSelectedRightPanelTab(RightPanelTabName.ViewDetails);
  };

  const handleModelDetailsSelect = (model: FileFolderItem): void => {
    setSelectedModelDetails(model);
    setSelectedRightPanelTab(RightPanelTabName.ModelDetails);
  };

  const handleRunDetailsSelect = (run: RunDetailsSelection): void => {
    setSelectedRunDetails(run);
    setSelectedRightPanelTab(RightPanelTabName.RunDetails);
  };

  const handleMissionDetailsSelect = (
    mission: MissionDetailsSelection
  ): void => {
    setSelectedMissionDetails(mission);
    setSelectedRightPanelTab(RightPanelTabName.MissionDetails);
  };

  const handleSidePanelTabChange = (
    tab: SidePanelTabName,
    isOpen: boolean
  ): void => {
    setCreateTopicRequest(undefined);
    setSelectedSidePanelTab(isOpen ? tab : undefined);
  };

  // Mirror measurement-details: open on select, close on deselect if active.
  createEffect(
    on(
      () => featureExtraction.editingState.selectedFeature,
      (selectedFeature) => {
        if (selectedFeature) {
          setSelectedRightPanelTab(RightPanelTabName.FeatureProperties);
        } else if (
          selectedRightPanelTab() === RightPanelTabName.FeatureProperties
        ) {
          setSelectedRightPanelTab(undefined);
        }
      }
    )
  );

  // Close object properties panel when right panel tab is changed
  createEffect(
    on(selectedRightPanelTab, (tab) => {
      const cap = objectPropertiesPanelCapability();
      if (tab !== RightPanelTabName.ObjectProperties && cap?.isOpen()) {
        cap?.close();
      }
    })
  );

  // Handle object properties panel toggle from the object selection toolbar
  createEffect(
    on(
      () => objectPropertiesPanelCapability()?.isOpen(),
      (isOpen) => {
        if (isOpen) {
          setSelectedRightPanelTab(RightPanelTabName.ObjectProperties);
        } else if (
          selectedRightPanelTab() === RightPanelTabName.ObjectProperties
        ) {
          setSelectedRightPanelTab(undefined);
        }
      }
    )
  );

  // Open the data panel if closed and no data loaded
  createRoot((dispose) => {
    createEffect(
      on(
        () =>
          (props.urlSyncComplete?.() ?? true) &&
          dataState.mission?.state === LoadState.LoadComplete &&
          dataState.trajectory?.state === LoadState.LoadComplete &&
          dataState.tcFileFolder?.state === LoadState.LoadComplete &&
          dataState.pointCloud?.state === LoadState.LoadComplete,
        (ready) => {
          if (!ready) return;

          const itemsLoadingOrLoaded =
            (dataState?.mission?.state === LoadState.LoadComplete &&
              Object.values(dataState.mission.items).some((i) =>
                isItemLoadingOrLoaded(i.loadState)
              )) ||
            (dataState?.trajectory?.state === LoadState.LoadComplete &&
              Object.values(dataState.trajectory.items).some((i) =>
                isItemLoadingOrLoaded(i.loadState)
              )) ||
            (dataState?.tcFileFolder?.state === LoadState.LoadComplete &&
              Object.values(dataState.tcFileFolder.items).some((i) =>
                isItemLoadingOrLoaded(i.loadState)
              )) ||
            (dataState?.pointCloud?.state === LoadState.LoadComplete &&
              Object.values(dataState.pointCloud.items).some((i) =>
                isItemLoadingOrLoaded(i.loadState)
              ));

          if (itemsLoadingOrLoaded) {
            dispose();
            return;
          }

          // Evaluated late to reduce e2e test flakiness, because of fast navigation
          if (isSidePanelOpen()) {
            dispose();
            return;
          }

          setSelectedSidePanelTab(SidePanelTabName.ProjectData);
          dispose();
        }
      )
    );
  });

  onMount(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    onCleanup(() => {
      window.removeEventListener('resize', handleResize);
    });
  });

  function clampPanelWidth(panelWidth: number): number {
    if (panelWidth > MAX_PANEL_WIDTH_PX) return MAX_PANEL_WIDTH_PX;
    if (panelWidth < MIN_PANEL_WIDTH_PX) return MIN_PANEL_WIDTH_PX;
    return panelWidth;
  }

  return (
    <>
      <ErrorBoundaryHandler>
        <PaneContextProvider>
          <div ref={setPortalMount} class="flex h-screen flex-col">
            <PortalMountProvider mount={() => portalMount()}>
              <div class="relative z-dropdown">
                <TrimblePageHeader
                  appName={t('mobileMapping.projectNumarkHeaderTitle')}
                  projectExplorerUrl={selectedProjectExplorerUrl()}
                  helpUrl={props.helpUrl}
                  profileUrl={props.profileUrl}
                />
              </div>
              <Show
                when={selectedProject()}
                fallback={<div class="min-h-0 flex-1 bg-trimble-gray-150" />}
              >
                <MainToolbar
                  ariaLabel={t('mobileMapping.globalToolbar.label')}
                  items={globalToolbarItems()}
                />
                <div data-testid="main-window" class="min-h-0 flex-1">
                  <Resizable
                    orientation="horizontal"
                    class="h-full"
                    sizes={paneSizes()}
                    onSizesChange={onSizesChange}
                    handleCursorStyle={false}
                  >
                    <Resizable.Panel
                      class={`flex items-center h-full bg-gradient-to-b from-gray-light to-gray-0 dark:from-gray-9 dark:to-gray-10`}
                    >
                      <div class="h-full w-full overflow-hidden panel-shadow relative z-header">
                        <SidePanel
                          selectedTab={selectedSidePanelTab()}
                          onTabChange={handleSidePanelTabChange}
                          onViewDetailsSelect={handleViewDetailsSelect}
                          onCreateViewClick={() =>
                            setSelectedRightPanelTab(
                              RightPanelTabName.CreateView
                            )
                          }
                          onModelDetailsSelect={handleModelDetailsSelect}
                          onRunDetailsSelect={handleRunDetailsSelect}
                          onMissionDetailsSelect={handleMissionDetailsSelect}
                          issueManagementCreateTopicRequest={createTopicRequest()}
                          onIssueManagementCreateTopicRequestConsumed={() =>
                            setCreateTopicRequest(undefined)
                          }
                        />
                      </div>
                    </Resizable.Panel>
                    {isSidePanelOpen() ? (
                      <Resizable.Handle
                        aria-label="Resize Side Panel"
                        class="group basis-1 py-[3px] cursor-col-resize bg-gradient-to-b from-gray-light to-gray-0 dark:from-gray-9 dark:to-gray-10"
                        onDblClick={() => {
                          setSidePanelWidthPx(DEFAULT_PANEL_WIDTH_PX);
                        }}
                      >
                        <div class="size-full rounded-sm transition-colors corvu-group-active:bg-current corvu-group-dragging:bg-current" />
                      </Resizable.Handle>
                    ) : null}
                    <Resizable.Panel class="relative h-full overflow-y-auto overflow-x-hidden">
                      <Web3DContainer web3DPath={props.web3DPath} />
                      <Show when={isLineDetectionEnabled()}>
                        <LineDetectionSettingsPanel />
                      </Show>
                    </Resizable.Panel>
                    {isRightPanelOpen() ? (
                      <Resizable.Handle
                        aria-label="Resize Web3D Container"
                        class="group basis-1 py-[3px] cursor-col-resize bg-gradient-to-b from-gray-light to-gray-0 dark:from-gray-9 dark:to-gray-10"
                        onDblClick={() => {
                          setRightPanelWidthPx(DEFAULT_PANEL_WIDTH_PX);
                        }}
                      >
                        <div class="size-full rounded-sm transition-colors corvu-group-active:bg-current corvu-group-dragging:bg-current" />
                      </Resizable.Handle>
                    ) : null}
                    <Resizable.Panel
                      class={`flex items-center h-full bg-gradient-to-b from-gray-light to-gray-0 dark:from-gray-9 dark:to-gray-10`}
                    >
                      <div
                        class={`${
                          isRightPanelOpen() ? 'h-full' : 'h-fit'
                        } w-full overflow-hidden panel-shadow relative z-header`}
                      >
                        <RightPanel
                          activeTab={activeRightPanelTab()}
                          onTabChange={setSelectedRightPanelTab}
                        />
                      </div>
                    </Resizable.Panel>
                  </Resizable>
                </div>
              </Show>
            </PortalMountProvider>
          </div>
        </PaneContextProvider>
      </ErrorBoundaryHandler>
      <Show
        when={
          fxWorkspace.isEnabled() && fxWorkspace.isNewExtractionDialogOpen()
        }
      >
        <NewExtractionDialog show={fxWorkspace.isNewExtractionDialogOpen()} />
      </Show>
    </>
  );
};
